import React, { memo, useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import { SELECT_STYLES } from '../../../constants';
import Button from '../../../../../components/Button';
import useTrackDeps from '../../../../../hooks/useTrackDeps';
import Tooltip from '../../../../../components/Tooltip';

type SelectOption = { label: string; value: string };

export const OverridesSelect = ({
  visual,
  visual_id,
  current,
  options,
  handleSaveOverride,
  loading,
  onOverrideChange,
  onOverrideSave,
  onOverrideCancel,
  hasUnsavedChanges,
}) => {
  const [selected, setSelected] = useState(() => current);
  const [localValue, setLocalValue] = useState(() => current?.value || null);

  useEffect(() => {
    if (current?.value !== localValue && !hasUnsavedChanges) {
      setSelected(current);
      setLocalValue(current?.value || null);
    }
  }, [current, localValue, hasUnsavedChanges]);

  const handleSelect = useCallback(
    (selection: SelectOption) => {
      console.log('handleSelect called with:', selection);
      setSelected(selection);
      setLocalValue(selection?.value || null);

      if (onOverrideChange) {
        onOverrideChange(visual_id);
      }
    },
    [visual_id, onOverrideChange]
  );

  const handleSave = useCallback(() => {
    handleSaveOverride({
      label: selected?.value,
      visual: visual_id,
      ad_name: visual.ad_name,
    });
    if (onOverrideSave) {
      onOverrideSave(visual_id);
    }
    setLocalValue(selected?.value || null);
  }, [selected, visual_id, visual.ad_name, handleSaveOverride, onOverrideSave]);

  const handleCancel = useCallback(() => {
    setSelected(current);
    setLocalValue(current?.value || null);
    if (onOverrideCancel) {
      onOverrideCancel(visual_id);
    }
  }, [current, visual_id, onOverrideCancel]);

  return (
    <div
      className="override-select d-flex"
      style={{ flexWrap: 'wrap', gap: '0.5rem' }}
    >
      <span className="lbl-display">
        {hasUnsavedChanges && selected?.value !== current?.value ? (
          <>
            {current?.value || 'None'} -&gt;{' '}
            <span className="lbl-display has-changed">
              {selected?.value || 'None'}
            </span>
          </>
        ) : (
          selected?.value || 'None'
        )}
      </span>
      <Select
        isClearable
        options={options}
        styles={SELECT_STYLES}
        placeholder="Select label"
        value={selected || null}
        isSearchable
        onChange={handleSelect}
        title={selected?.value}
        instanceId={`override-select-${visual_id}`}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuShouldScrollIntoView={false}
      />
      <Button onClick={handleSave} disabled={loading} loading={loading}>
        Save
      </Button>
      {hasUnsavedChanges && (
        <Button onClick={handleCancel} appearance="subtle" loading={false}>
          Cancel
        </Button>
      )}
    </div>
  );
};

interface OverridesSelectWrapperProps {
  visual: any;
  current: { label: string; value: string } | null;
  options: Array<{ label: string; value: string }>;
  handleOverride: (params: {
    label: string;
    visual: string;
    ad_name: string;
  }) => void;
  loading: boolean;
  onOverrideChange: (visualId: string) => void;
  onOverrideSave: (visualId: string) => void;
  onOverrideCancel: (visualId: string) => void;
  hasUnsavedChanges: boolean;
}

export const OverridesSelectWrapper = memo(
  ({
    visual,
    current,
    options,
    handleOverride,
    loading,
    onOverrideChange,
    onOverrideSave,
    onOverrideCancel,
    hasUnsavedChanges,
  }: OverridesSelectWrapperProps) => {
    return (
      <div className="overrides">
        <div className="d-flex justify-content-between">
          <label>Override label:</label>
          <p>{}</p>
          {hasUnsavedChanges && (
            <Tooltip content="Unsaved override">
              <div className="unsaved-indicator">
                <i className="fa-solid fa-circle-exclamation"></i>
              </div>
            </Tooltip>
          )}
        </div>
        <OverridesSelect
          visual={visual}
          visual_id={visual.darwin_visual_id}
          current={current}
          options={options}
          handleSaveOverride={handleOverride}
          onOverrideChange={onOverrideChange}
          onOverrideSave={onOverrideSave}
          onOverrideCancel={onOverrideCancel}
          loading={loading}
          hasUnsavedChanges={hasUnsavedChanges}
        />
      </div>
    );
  }
);

OverridesSelectWrapper.displayName = 'OverridesSelectWrapper';
